<div class="flex flex-1 flex-col h-full w-full gap-2 p-3">
    <!-- Top Bar -->
    <div class="flex items-center gap-3 pb-2">
        <app-button variant="filled" severity="primary" icon="pi pi-plus" iconPosition="left"
            (clicked)="onCreateTemplate()" label="Create template">
        </app-button>

        <div class="w-[25%]">
            <app-search-bar placeholder="Search template name" [(ngModel)]="searchValue"
                (searchChange)="onSearchChange()" width="100%"></app-search-bar>
        </div>

        <div class="flex items-center gap-2 ml-auto">

            <!-- View Toggle -->
            <app-select-button [options]="viewModeOptions" [(ngModel)]="viewMode"
                (selectionChange)="onViewModeChange($event)" size="small">
            </app-select-button>

            <!-- Sort -->
            <app-sort-popover [fields]="sortFields" [(sortField)]="sortField" [(sortOrder)]="sortOrder"
                (sortChange)="sortTemplates()" />

            <!-- Filter -->
            <app-button variant="outlined" severity="contrast" class="border border-arena-gray-200 rounded-xl"
                icon="pi pi-filter" iconPosition="left" (clicked)="onFilterClick()">
            </app-button>
        </div>
    </div>

    <!-- Bottom Action Bar -->
    @if(selectedTemplatesCount > 0) {
    <div class="flex items-center justify-between gap-4 p-2 bg-white rounded-base border border-gray-200 shadow-sm">
        <div class="flex items-center gap-4">
            <div class="border-r-2 border-arena-gray-200 pr-4">
                <span class="text-sm text-gray-700 ">{{ selectedTemplatesCount }} Item{{ selectedTemplatesCount > 1 ?
                    's' :
                    '' }} is selected</span>
            </div>
            <app-button variant="outlined" class="border border-arena-gray-200" icon="pi pi-download"
                iconPosition="left" (clicked)="onBulkDownload()" label="Download">
            </app-button>
        </div>
        <div class="flex items-center gap-2">

            <app-button variant="outlined" severity="danger" icon="pi pi-trash" iconPosition="left"
                (clicked)="onBulkDelete()" label="Delete">
            </app-button>
        </div>
    </div>
    }
    @if(templates.length > 0) {
    <!-- Templates Grid/List -->
    <div class="flex-1 min-h-0 flex flex-col">
        <app-grid-list-view #gridListView [items]="templates" [viewMode]="viewMode"
            [gridItemTemplate]="gridItemTemplate" [dataKey]="'_id'" [scrollable]="true" [rows]="rows"
            [rowsPerPageOptions]="[20, 30, 40]" [useDataTable]="viewMode === 'list'" [tableColumns]="tableColumns"
            [tableConfig]="tableConfig" [customTemplates]="templatesMap" [totalRecords]="totalRecords" [first]="first"
            [showPaginator]="true" (pageChange)="onPageChange($event)"
            (selectedItemsChange)="onTableSelectionChange($event)" (rowClick)="onTemplateClick($event)"
            [parentClass]="'flex-1 flex flex-col min-h-0'" [itemsLoading]="tableConfig.loading || false" [gridCols]="1"
            [gridColsSm]="2" [gridColsMd]="3" [gridColsLg]="3" [gridColsXl]="4" [gridCols2xl]="4">
        </app-grid-list-view>

        <!-- Grid Item Template -->
        <ng-template #gridItemTemplate let-template>
            <div class="border border-gray-200 shadow-sm flex flex-col cursor-pointer hover:shadow-md transition min-w-[15rem] h-full bg-white rounded-2xl"
                [class.border-orange-300]="tableConfig.selectable && isTemplateSelected(template._id)"
                [class.bg-orange-50]="tableConfig.selectable && isTemplateSelected(template._id)"
                (click)="onTemplateClick(template)">
                <div class="flex flex-col flex-1 p-3 gap-1 justify-between ">
                    <!-- Header -->
                    <div class="flex justify-between items-center pb-1">
                        <div class="flex flex-1 items-center gap-2">
                            @if(tableConfig.selectable) {
                            <p-checkbox [ngModel]="isTemplateSelected(template._id)"
                                (ngModelChange)="toggleTemplateSelection(template._id)" [binary]="true"
                                [inputId]="'template-' + template._id" (click)="$event.stopPropagation()">
                            </p-checkbox>
                            }
                            <div class="rounded-md py-0.5 px-3 flex items-center theme-text-border w-auto max-w-[40%]">
                                <span class="text-xs font-medium truncate block max-w-full"
                                    [pTooltip]="template?.templateId" tooltipPosition="top">
                                    {{ template?.templateId }}
                                </span>
                            </div>
                        </div>

                        <div (click)="$event.stopPropagation()">
                            <!-- Show failed icon if generation is paused due to credit exhaustion -->
                            @if(template.generationStatus === TEMPLATE_GENERATION_STATUS.CREDITS_EXHAUSTED) {
                            <div class="flex items-center justify-center w-8 h-8" pTooltip="Credits exhausted"
                                tooltipPosition="top">
                                <app-icon type="pi pi-exclamation-circle" source="primeng" size="default"
                                    class="text-red-500" />
                            </div>
                            }
                            <!-- Show status badge if generating -->
                            @else if(isTemplateGenerating(template)) {
                            <div class="flex items-center justify-center w-8 h-8">
                                <img src="icons/template-library/generation_in_progress.svg" alt="Generating"
                                    class="w-4 h-4 animate-spin">
                            </div>
                            }
                            <!-- Show tick icon if newly completed -->
                            @else if(shouldShowCompletionTick(template._id)) {
                            <div class="flex items-center justify-center w-8 h-8">
                                <img src="icons/template-library/generation_done.svg" alt="Generating" class="w-6 h-6">
                            </div>
                            }
                            <!-- Show menu for all other cases -->
                            @else {
                            <app-menu #templateMenu [items]="getTemplateMenuItems(template)"
                                [visible]="openTemplateMenuId === template._id"
                                (visibleChange)="onTemplateMenuVisibilityChange($event, template._id)"
                                (itemSelected)="handleTemplateMenuAction($event, template)">
                                <i class="pi pi-ellipsis-v text-gray-600 icon-sm cursor-pointer rounded p-1"
                                    (click)="openTemplateMenu(template._id, templateMenu, $event); $event.stopPropagation()"></i>
                            </app-menu>
                            }
                        </div>
                    </div>

                    <!-- Template Name -->
                    <div class="text-sm font-semibold text-gray-900 line-clamp-1 truncate max-w-full "
                        [title]="template.name" pTooltip="{{ template.name }}" tooltipPosition="top">
                        {{ template.name }}
                    </div>

                    <!-- Template Usage -->
                    <div class="flex items-center gap-2">
                        <span class="text-xs text-gray-600">Template usage</span>
                        <span class="rounded-md py-0.5 px-3 bg-orange-100 text-orange-600 text-xs font-medium">
                            {{ template.timesUsed }}
                        </span>
                    </div>
                </div>

                <!-- Footer -->
                <div class="flex items-center justify-between gap-0.5 p-3 text-xs border-t border-gray-200">
                    <span class="font-light text-gray-600">Created on <span class="font-semibold text-gray-900">{{
                            template.createdAt | dateFormatter: 'Do MMM, YYYY' }}</span></span>
                    <div class="flex items-center gap-2">
                        <div class="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <span class="text-xs font-semibold text-blue-700">{{
                                template.createdBy.fullName.charAt(0)}}</span>
                        </div>
                        <app-user-name-display [user]="template.createdBy"></app-user-name-display>
                    </div>
                </div>
            </div>
        </ng-template>

        <!-- Table Templates -->
        <!-- Template ID Template -->
        <ng-template #templateIdTemplateRef let-template="$implicit">
            <div class="flex items-center gap-2">
                <span class="text-sm text-gray-900 font-medium">{{ template.templateId }}</span>
            </div>
        </ng-template>

        <!-- Template Name Template -->
        <ng-template #templateNameTemplateRef let-template="$implicit">
            <div class="text-sm text-gray-900 font-medium">
                {{ template.name }}
            </div>
        </ng-template>

        <!-- Template Usage Template -->
        <ng-template #templateUsageTemplateRef let-template="$implicit">
            <div class="text-sm text-gray-900">
                {{ template.timesUsed }}
            </div>
        </ng-template>

        <!-- Created Date Template -->
        <ng-template #createdDateTemplateRef let-template="$implicit">
            <div class="flex items-center gap-2 text-sm text-gray-900">
                <i class="pi pi-calendar text-gray-500 text-xs"></i>
                <span>{{ template.createdAt | date: 'dd MMM yyyy' }}</span>
            </div>
        </ng-template>

        <!-- Created By Template -->
        <ng-template #createdByTemplateRef let-template="$implicit">
            <div class="flex items-center gap-2">
                <div class="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span class="text-xs font-semibold text-blue-700">{{ (template.createdBy.fullName ||
                        template.createdBy.firstName || 'U').charAt(0) }}</span>
                </div>
                <app-user-name-display [user]="template.createdBy">
                </app-user-name-display>
            </div>
        </ng-template>

        <!-- Actions Template -->
        <ng-template #actionsTemplateRef let-template="$implicit">
            <div class="flex items-center justify-center" (click)="$event.stopPropagation()">
                <!-- Show failed icon if generation is paused due to credit exhaustion -->
                @if(template.generationStatus === TEMPLATE_GENERATION_STATUS.CREDITS_EXHAUSTED) {
                <div class="flex items-center justify-center w-8 h-8" pTooltip="Credits exhausted"
                    tooltipPosition="top">
                    <app-icon type="pi pi-exclamation-circle" source="primeng" size="default" class="text-red-500" />
                </div>
                }
                <!-- Show status badge if generating -->
                @else if(isTemplateGenerating(template)) {
                <div class="flex items-center justify-center w-8 h-8">
                    <img src="icons/template-library/generation_in_progress.svg" alt="Generating"
                        class="w-4 h-4 animate-spin">
                </div>
                }
                <!-- Show tick icon if newly completed -->
                @else if(shouldShowCompletionTick(template._id)) {
                <div class="flex items-center justify-center w-8 h-8">
                    <img src="icons/template-library/generation_done.svg" alt="Generating" class="w-6 h-6">
                </div>
                }
                <!-- Show menu for all other cases -->
                @else {
                <app-menu #templateMenuTable [items]="getTemplateMenuItems(template)"
                    [visible]="openTemplateMenuId === template._id"
                    (visibleChange)="onTemplateMenuVisibilityChange($event, template._id)"
                    (itemSelected)="handleTemplateMenuAction($event, template)">
                    <i class="pi pi-ellipsis-v text-gray-600 text-sm cursor-pointer hover:bg-gray-100 rounded p-1"
                        (click)="openTemplateMenu(template._id, templateMenuTable, $event); $event.stopPropagation()"></i>
                </app-menu>
                }
            </div>
        </ng-template>
    </div>
    }@else{
    <div class="flex flex-col items-center flex-1 justify-center gap-4">
        <img class="w-[50%] h-[50%] object-contain" src="images/templates_not_found.png" alt="image">
        <p class="text-sm text-gray-500">No templates found</p>
    </div>
    }
</div>

<!-- Filter Drawer -->
<p-drawer [(visible)]="filterPanelVisible" position="right" class="w-[22rem] filter-drawer">
    <ng-template pTemplate="headless">
        <div class="flex flex-col h-full">
            <div class="flex items-center justify-between border-b p-4">
                <h2 class="text-lg font-semibold">Filter</h2>
                <app-button icon="pi pi-times" (clicked)="filterPanelVisible = false" variant="filled"
                    severity="outline" />
            </div>
            <div class="flex-1 overflow-y-auto space-y-6 p-4">
                <!-- Created Date Filter -->
                <div class="flex flex-col gap-3">
                    <label class="text-sm font-medium text-gray-700">Created date</label>
                    <app-datepicker [(value)]="createdDateRange" [config]="{
                        selectionMode: 'range',
                        showIcon: true,
                        dateFormat: 'dd M yy',
                        placeholder: 'Select date range',
                        appendTo: 'body',
                        showOnFocus: false,
                        maxDate: currentDate
                    }" (onChange)="onCreatedDateRangeChange()" />

                    <!-- Quick Select Buttons -->
                    <div class="flex flex-wrap gap-2">
                        <app-button variant="outlined" severity="contrast" [label]="'Today'"
                            [icon]="createdDateQuickOption === 'today' ? 'pi pi-times' : ''" [iconPosition]="'right'"
                            [iconSource]="'primeng'" [iconSize]="'sm'"
                            [class]="createdDateQuickOption === 'today' 
                                ? '!px-3 !py-1.5 !text-sm !rounded-full !border-orange-500 !bg-orange-100 hover:!bg-orange-200 !text-gray-900' 
                                : '!px-3 !py-1.5 !text-sm !rounded-full !border-gray-300 !bg-white hover:!bg-gray-50 !text-gray-900'"
                            (clicked)="selectCreatedDateQuickOption('today')">
                        </app-button>
                        <app-button variant="outlined" severity="contrast" [label]="'7 days'"
                            [icon]="createdDateQuickOption === '7days' ? 'pi pi-times' : ''" [iconPosition]="'right'"
                            [iconSource]="'primeng'" [iconSize]="'sm'"
                            [class]="createdDateQuickOption === '7days' 
                                ? '!px-3 !py-1.5 !text-sm !rounded-full !border-orange-500 !bg-orange-100 hover:!bg-orange-200 !text-gray-900' 
                                : '!px-3 !py-1.5 !text-sm !rounded-full !border-gray-300 !bg-white hover:!bg-gray-50 !text-gray-900'"
                            (clicked)="selectCreatedDateQuickOption('7days')">
                        </app-button>
                        <app-button variant="outlined" severity="contrast" [label]="'30 days'"
                            [icon]="createdDateQuickOption === '30days' ? 'pi pi-times' : ''" [iconPosition]="'right'"
                            [iconSource]="'primeng'" [iconSize]="'sm'"
                            [class]="createdDateQuickOption === '30days' 
                                ? '!px-3 !py-1.5 !text-sm !rounded-full !border-orange-500 !bg-orange-100 hover:!bg-orange-200 !text-gray-900' 
                                : '!px-3 !py-1.5 !text-sm !rounded-full !border-gray-300 !bg-white hover:!bg-gray-50 !text-gray-900'"
                            (clicked)="selectCreatedDateQuickOption('30days')">
                        </app-button>
                    </div>

                    <!-- Clear button for Created Date -->
                    @if(createdDateRange) {
                    <button (click)="clearCreatedDateFilter()"
                        class="text-xs text-gray-600 hover:text-gray-900 self-start">
                        Clear
                    </button>
                    }
                </div>

                <!-- Last Modified Date Filter -->
                <div class="flex flex-col gap-3">
                    <label class="text-sm font-medium text-gray-700">Last modified date</label>
                    <app-datepicker [(value)]="lastModifiedDateRange" [config]="{
                        selectionMode: 'range',
                        showIcon: true,
                        dateFormat: 'dd M yy',
                        placeholder: 'Select date range',
                        appendTo: 'body',
                        showOnFocus: false,
                        maxDate: currentDate
                    }" (onChange)="onLastModifiedDateRangeChange()" />

                    <!-- Quick Select Buttons -->
                    <div class="flex flex-wrap gap-2">
                        <app-button variant="outlined" severity="contrast" [label]="'Today'"
                            [icon]="lastModifiedDateQuickOption === 'today' ? 'pi pi-times' : ''"
                            [iconPosition]="'right'" [iconSource]="'primeng'" [iconSize]="'sm'"
                            [class]="lastModifiedDateQuickOption === 'today' 
                                ? '!px-3 !py-1.5 !text-sm !rounded-full !border-orange-500 !bg-orange-100 hover:!bg-orange-200 !text-gray-900' 
                                : '!px-3 !py-1.5 !text-sm !rounded-full !border-gray-300 !bg-white hover:!bg-gray-50 !text-gray-900'"
                            (clicked)="selectLastModifiedDateQuickOption('today')">
                        </app-button>
                        <app-button variant="outlined" severity="contrast" [label]="'7 days'"
                            [icon]="lastModifiedDateQuickOption === '7days' ? 'pi pi-times' : ''"
                            [iconPosition]="'right'" [iconSource]="'primeng'" [iconSize]="'sm'"
                            [class]="lastModifiedDateQuickOption === '7days' 
                                ? '!px-3 !py-1.5 !text-sm !rounded-full !border-orange-500 !bg-orange-100 hover:!bg-orange-200 !text-gray-900' 
                                : '!px-3 !py-1.5 !text-sm !rounded-full !border-gray-300 !bg-white hover:!bg-gray-50 !text-gray-900'"
                            (clicked)="selectLastModifiedDateQuickOption('7days')">
                        </app-button>
                        <app-button variant="outlined" severity="contrast" [label]="'30 days'"
                            [icon]="lastModifiedDateQuickOption === '30days' ? 'pi pi-times' : ''"
                            [iconPosition]="'right'" [iconSource]="'primeng'" [iconSize]="'sm'"
                            [class]="lastModifiedDateQuickOption === '30days' 
                                ? '!px-3 !py-1.5 !text-sm !rounded-full !border-orange-500 !bg-orange-100 hover:!bg-orange-200 !text-gray-900' 
                                : '!px-3 !py-1.5 !text-sm !rounded-full !border-gray-300 !bg-white hover:!bg-gray-50 !text-gray-900'"
                            (clicked)="selectLastModifiedDateQuickOption('30days')">
                        </app-button>
                    </div>

                    <!-- Clear button for Last Modified Date -->
                    @if(lastModifiedDateRange) {
                    <button (click)="clearLastModifiedDateFilter()"
                        class="text-xs text-gray-600 hover:text-gray-900 self-start">
                        Clear
                    </button>
                    }
                </div>

                <!-- Author Filter -->
                <div class="flex flex-col gap-3">
                    <label class="text-sm font-medium text-gray-700">Author</label>
                    <app-multi-select [(value)]="selectedAuthorIds" [options]="authorOptions" optionLabel="label"
                        optionValue="value" [config]="{
                            placeholder: 'Select author',
                            filter: true,
                            searchPlaceholder: 'Search author',
                            panelStyleClass: 'max-h-48 overflow-y-auto',
                            styleClass: 'w-full border border-gray-300 rounded-base'
                        }">
                    </app-multi-select>

                    <!-- Clear button for Author -->
                    @if(selectedAuthorIds.length > 0) {
                    <button (click)="clearAuthorFilter()" class="text-xs text-gray-600 hover:text-gray-900 self-start">
                        Clear
                    </button>
                    }
                </div>
            </div>
            <div class="flex items-center px-4 py-3 border-t gap-2 w-full">
                <app-button label="Clear all" (clicked)="clearAllFilters()" variant="outlined" severity="contrast"
                    [disabled]="!hasActiveFilters" class="flex-1 w-full">
                </app-button>
                <app-button label="Apply filter" (clicked)="applyFilters()" variant="filled" severity="primary"
                    [disabled]="!hasActiveFilters" class="flex-1 w-full">
                </app-button>
            </div>
        </div>
    </ng-template>
</p-drawer>

<!-- Create Template Dialog -->
<app-dialog [(visible)]="showCreateTemplateDialog" (closed)="onCloseCreateTemplateDialog()" class="max-w-[42rem]"
    [fullscreenOnMobile]="true">
    <ng-template #headerTemplate>
        <hlm-dialog-header>
            <div class="flex items-center gap-2">
                @if(showUploadSection || showFilePreview || showGenerateTemplateSection) {
                <button (click)="onBack()" class="text-gray-600 hover:text-gray-900 transition-colors">
                    <app-icon type="pi pi-chevron-left" source="primeng"></app-icon>
                </button>
                }
                <h2 class="text-lg font-semibold text-gray-900">
                    {{ showFilePreview ? 'Upload template' : showGenerateTemplateSection ? 'Generate template' :
                    'Create template' }}
                </h2>
            </div>
        </hlm-dialog-header>
    </ng-template>
    <ng-template #bodyTemplate>
        <div class="flex flex-col gap-4 p-4 overflow-y-auto">
            @if(!showUploadSection && !showGenerateTemplateSection) {
            <!-- Form Fields and Creation Method Selection -->
            <!-- Form Fields -->
            <div class="flex flex-col gap-4">
                <!-- Template Name -->
                <div class="flex flex-col gap-1.5">
                    <label class="text-sm font-medium text-gray-600">
                        Template name<span class="text-red-500 ml-1">*</span>
                    </label>
                    <input pInputText class="w-full border rounded-base p-1 border-gray-300" [(ngModel)]="templateName"
                        name="templateName" placeholder="Type here" [style]="{ outline: 'none' }" required />
                </div>

                <!-- Template ID -->
                <div class="flex flex-col gap-1.5">
                    <label class="text-sm font-medium text-gray-600">
                        Template ID<span class="text-red-500 ml-1">*</span>
                    </label>
                    <input pInputText class="w-full border rounded-base p-1 border-gray-300" [(ngModel)]="templateId"
                        name="templateId" placeholder="Type here" [style]="{ outline: 'none' }" required />
                </div>

                <!-- Description -->
                <div class="flex flex-col gap-1.5">
                    <label class="text-sm font-medium text-gray-600">
                        Description
                    </label>
                    <input pInputText class="w-full border rounded-base p-1 border-gray-300" [(ngModel)]="description"
                        name="description" placeholder="Type here" [style]="{ outline: 'none' }" />
                </div>

                <!-- Document Type -->
                <div class="flex flex-col gap-1.5">
                    <label class="text-sm font-medium text-gray-600">
                        Document type<span class="text-red-500 ml-1">*</span>
                    </label>
                    <app-select [(value)]="selectedDocumentTypeId" [options]="documentTypeOptions"
                        [config]="documentTypeConfig" name="documentType">
                    </app-select>
                </div>
            </div>

            <!-- Creation Method Selection -->
            <div class="flex flex-col gap-3">
                <h3 class="text-sm font-semibold">How would you like to create template?</h3>

                <div class="grid grid-cols-2 gap-3">
                    <!-- Extract from Document -->
                    <div class="gradient-card rounded-lg p-4 cursor-pointer transition-all hover:shadow-md flex flex-col relative overflow-hidden"
                        [class.theme-text-border]="selectedCreationMethod === TEMPLATE_CREATION_METHOD.EXTRACT_FROM_DOCUMENT"
                        [class.border-gray-200]="selectedCreationMethod !== TEMPLATE_CREATION_METHOD.EXTRACT_FROM_DOCUMENT"
                        [class.border-2]="selectedCreationMethod === TEMPLATE_CREATION_METHOD.EXTRACT_FROM_DOCUMENT"
                        (click)="onSelectCreationMethod(TEMPLATE_CREATION_METHOD.EXTRACT_FROM_DOCUMENT)">
                        <div class="relative z-10 flex flex-col gap-1">
                            <div>
                                <img src="icons/template-library/extract_from_document.svg" alt="Extract from document"
                                    class="w-6 h-6">
                            </div>
                            <div class="pt-1">
                                <span class="text-sm font-medium text-gray-900">Extract from document</span>
                            </div>
                            <p class="text-xs text-gray-600">
                                Upload an existing document and AI will automatically extract and structure it for you.
                            </p>
                        </div>
                    </div>

                    <!-- Generate Template -->
                    <div class="gradient-card rounded-lg p-4 cursor-pointer transition-all hover:shadow-md flex flex-col relative overflow-hidden"
                        [class.theme-text-border]="selectedCreationMethod === TEMPLATE_CREATION_METHOD.GENERATE_TEMPLATE"
                        [class.border-gray-200]="selectedCreationMethod !== TEMPLATE_CREATION_METHOD.GENERATE_TEMPLATE"
                        [class.border-2]="selectedCreationMethod === TEMPLATE_CREATION_METHOD.GENERATE_TEMPLATE"
                        (click)="onSelectCreationMethod(TEMPLATE_CREATION_METHOD.GENERATE_TEMPLATE)">
                        <div class="relative z-10 flex flex-col gap-1">
                            <div>
                                <img src="icons/template-library/generate_from_template.svg" alt="Generate Template"
                                    class="w-6 h-6">
                            </div>
                            <div>
                                <span class="text-sm font-medium text-gray-900">Generate template</span>
                            </div>
                            <p class="text-xs text-gray-600">
                                Create a new template effortlessly with AI in just a few clicks
                            </p>
                        </div>
                    </div>

                    <!-- Manual Create -->
                    <div class="gradient-card rounded-lg p-4 cursor-pointer transition-all hover:shadow-md flex flex-col relative overflow-hidden"
                        [class.theme-text-border]="selectedCreationMethod === TEMPLATE_CREATION_METHOD.MANUAL_CREATE"
                        [class.border-gray-200]="selectedCreationMethod !== TEMPLATE_CREATION_METHOD.MANUAL_CREATE"
                        [class.border-2]="selectedCreationMethod === TEMPLATE_CREATION_METHOD.MANUAL_CREATE"
                        (click)="onSelectCreationMethod(TEMPLATE_CREATION_METHOD.MANUAL_CREATE)">
                        <div class="relative z-10 flex flex-col gap-1">
                            <div>
                                <img src="icons/template-library/manual_create.svg" alt="Manual Create" class="w-5 h-5">
                            </div>
                            <div class="pt-1">
                                <span class="text-sm font-medium text-gray-900">Manual create</span>
                            </div>
                            <p class="text-xs text-gray-600">
                                Manually create your template from scratch with full control and flexibility.
                            </p>
                        </div>
                    </div>

                    <!-- Upload Template -->
                    <div class="gradient-card rounded-lg p-4 cursor-pointer transition-all hover:shadow-md flex flex-col relative overflow-hidden"
                        [class.theme-text-border]="selectedCreationMethod === TEMPLATE_CREATION_METHOD.UPLOAD_TEMPLATE"
                        [class.border-gray-200]="selectedCreationMethod !== TEMPLATE_CREATION_METHOD.UPLOAD_TEMPLATE"
                        [class.border-2]="selectedCreationMethod === TEMPLATE_CREATION_METHOD.UPLOAD_TEMPLATE"
                        (click)="onSelectCreationMethod(TEMPLATE_CREATION_METHOD.UPLOAD_TEMPLATE)">
                        <div class="relative z-10 flex flex-col gap-1">
                            <div>
                                <img src="icons/template-library/upload_template.svg" alt="Upload Template"
                                    class="w-6 h-6">
                            </div>
                            <div class="">
                                <span class="text-sm font-medium text-gray-900">Upload template</span>
                            </div>
                            <p class="text-xs text-gray-600">
                                Upload an existing template, get started instantly, and make changes if needed.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            } @else if (showGenerateTemplateSection) {
            <!-- Generate Template Section -->
            <div class="flex flex-col gap-2">
                <div class="flex flex-col gap-1.5">
                    <label class="text-sm font-medium text-gray-700">
                        Describe what you want to generate
                    </label>
                    <textarea pInputTextarea [(ngModel)]="generatePrompt" name="generatePrompt"
                        class="w-full border rounded-base p-3 border-gray-300 min-h-[200px] resize-none"
                        [style]="{ outline: 'none' }" placeholder="Enter your description here..."></textarea>
                </div>

                <div class="flex items-center gap-1 text-sm text-gray-600">
                    <img src="icons/template-library/ai_icon.svg" alt="Inndoc AI" class="w-5 h-5">
                    <span class="italic theme-text">Powered by InnDoc AI</span>
                </div>
            </div>
            } @else {
            <!-- Upload Section -->
            @if(showFilePreview) {
            <!-- File Preview View -->
            <div class="flex flex-col gap-6 items-center py-4">
                <!-- Animation Area -->
                <div class="relative w-full flex items-center justify-center py-8">
                    @if (uploadingAnimation) {
                    <app-lottie-animation [animationData]="uploadingAnimation" [loop]="true" [autoplay]="true"
                        width="300px" height="300px">
                    </app-lottie-animation>
                    }
                </div>

                <!-- File Details -->
                <!-- Single file from Files tab -->
                @if(selectedExistingFile) {
                <div class="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                    <div class="flex items-center gap-3 flex-1 min-w-0">
                        <!-- File Icon -->
                        <div class="flex-shrink-0">
                            @if(selectedExistingFile.mimeType == "pdf"){
                            <i [innerHTML]="SVGS.PDF | sanitizeHtml" alt="" width="24" height="24"></i>
                            }@else if (selectedExistingFile.mimeType == "docx") {
                            <i [innerHTML]="SVGS.DOCX | sanitizeHtml" alt="" width="24" height="24"></i>
                            }
                        </div>
                        <!-- File Info -->
                        <div class="flex flex-col gap-1 min-w-0 flex-1">
                            <div class="text-sm font-medium text-gray-900 truncate" [title]="selectedExistingFile.name">
                                {{ selectedExistingFile.name }}
                            </div>
                            <div class="text-xs text-gray-500">
                                {{ selectedExistingFile.size | fileSize }}
                            </div>
                        </div>
                    </div>
                    <!-- Delete Button -->
                    <button (click)="onDeleteSelectedFile()"
                        class="flex-shrink-0 ml-3 p-2 hover:bg-gray-100 rounded transition-colors" type="button">
                        <i class="pi pi-trash text-red-500 text-sm"></i>
                    </button>
                </div>
                }

                <!-- Multiple files from Knowledge Base -->
                @if(selectedKnowledgeBaseFiles.length > 0) {
                <div class="w-full flex flex-col gap-2 max-h-[300px] overflow-y-auto">
                    @for(file of selectedKnowledgeBaseFiles; track file._id) {
                    <div class="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                        <div class="flex items-center gap-3 flex-1 min-w-0">
                            <!-- File Icon -->
                            <div class="flex-shrink-0">
                                @if(file.mimeType == "pdf"){
                                <i [innerHTML]="SVGS.PDF | sanitizeHtml" alt="" width="24" height="24"></i>
                                }@else if (file.mimeType == "docx") {
                                <i [innerHTML]="SVGS.DOCX | sanitizeHtml" alt="" width="24" height="24"></i>
                                }
                            </div>
                            <!-- File Info -->
                            <div class="flex flex-col gap-1 min-w-0 flex-1">
                                <div class="text-sm font-medium text-gray-900 truncate" [title]="file.name">
                                    {{ file.name }}
                                </div>
                                <div class="text-xs text-gray-500">
                                    {{ file.size | fileSize }}
                                </div>
                            </div>
                        </div>
                        <!-- Delete Button -->
                        <button (click)="onDeleteKnowledgeBaseFile(file._id)"
                            class="flex-shrink-0 ml-3 p-2 hover:bg-gray-100 rounded transition-colors" type="button">
                            <i class="pi pi-trash text-red-500 text-sm"></i>
                        </button>
                    </div>
                    }
                </div>
                }
            </div>
            } @else if(!showFileSelectionView) {
            <!-- Upload View -->

            <app-file-drop [fileTypes]="['.pdf', '.docx', '.zip']" [maxFileSize]="50 * 1000 * 1000" [multiple]="false"
                [existingFiles]="uploadedLocalFiles" (uploadData)="onExtractFromDocumentUpload($event)"
                (removeFile)="onRemoveLocalFile($event)">
            </app-file-drop>

            <!-- Choose from existing files button -->
            <div class="mt-3">
                <button
                    class="w-full flex items-center justify-between px-4 py-2 border border-gray-300 rounded-base hover:bg-gray-50 transition-colors"
                    (click)="openFileSelectionView()">
                    <span class="text-sm font-medium text-gray-700">Choose from existing files</span>
                    <i class="pi pi-chevron-right text-gray-500"></i>
                </button>
            </div>
            } @else {
            <!-- File Selection View -->
            <!-- Tabs for Files and Knowledge Base -->
            <div class="">
                <p-tabs [(value)]="activeTab" class="w-full">
                    <p-tablist>
                        <p-tab value="0">
                            <span class="font-medium">Files</span>
                        </p-tab>
                        <p-tab value="1">
                            <span class="font-medium">Knowledge base</span>
                        </p-tab>
                    </p-tablist>
                    <p-tabpanels>
                        <p-tabpanel value="0">
                            @if(fileSelectionLoading) {
                            <div class="flex items-center justify-center p-8">
                                <p class="text-sm text-gray-500">Loading files...</p>
                            </div>
                            } @else {
                            <!-- Search Bar -->
                            <div class="w-full mb-4">
                                <app-search-bar placeholder="Search with name or format"
                                    [(ngModel)]="fileSelectionSearchValue" (searchChange)="onFileSelectionSearch()"
                                    width="100%">
                                </app-search-bar>
                            </div>

                            <!-- Files Tab Content -->
                            <div class="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
                                @if(filteredFiles.length > 0) {
                                <!-- Files List -->
                                <div class="flex flex-col gap-2">
                                    @for(file of filteredFiles; track file._id) {
                                    <div class="flex items-center gap-3 p-3 rounded-lg border transition-colors"
                                        [class.bg-orange-50]="isFileSelected(file)"
                                        [class.border-orange-200]="isFileSelected(file)"
                                        [class.border-gray-200]="!isFileSelected(file)"
                                        [class.hover:bg-gray-50]="!isFileSelected(file)">
                                        <p-checkbox [ngModel]="isFileSelected(file)"
                                            (ngModelChange)="setFileSelectedState(file, $event)" [binary]="true"
                                            [inputId]="'file-' + file._id">
                                        </p-checkbox>
                                        <div class="flex-shrink-0">
                                            @if(file.mimeType == "pdf"){
                                            <i [innerHTML]="SVGS.PDF | sanitizeHtml" alt="" width="24" height="24"></i>
                                            }@else if (file.mimeType == "docx") {
                                            <i [innerHTML]="SVGS.DOCX | sanitizeHtml" alt="" width="24" height="24"></i>
                                            }
                                        </div>
                                        <div class="min-w-0 flex-1">
                                            <div class="text-sm font-medium text-gray-900 truncate"
                                                pTooltip="{{ file.name }}" tooltipPosition="top">
                                                {{ file.name }}
                                            </div>
                                            <div class="text-xs text-gray-500 truncate">
                                                {{ file.size | fileSize }}
                                            </div>
                                        </div>
                                    </div>
                                    }
                                </div>
                                } @else {
                                <div class="flex items-center justify-center p-8">
                                    <p class="text-sm text-gray-500">No files found</p>
                                </div>
                                }
                            </div>
                            }
                        </p-tabpanel>
                        <p-tabpanel value="1">
                            @if(fileSelectionLoading) {
                            <div class="flex items-center justify-center p-8">
                                <p class="text-sm text-gray-500">Loading knowledge bases...</p>
                            </div>
                            } @else {
                            <!-- Two Panel Layout for Knowledge Base -->
                            <div class="flex gap-4 h-[400px]">
                                <!-- Left Panel: Knowledge Bases List -->
                                <div class="w-1/2 flex flex-col border-r border-gray-200 pr-4">
                                    <!-- Search Bar -->
                                    <div class="w-full mb-4">
                                        <app-search-bar placeholder="Search knowledge base"
                                            [(ngModel)]="fileSelectionSearchValue"
                                            (searchChange)="onKnowledgeBaseSearch()" width="100%">
                                        </app-search-bar>
                                    </div>

                                    <!-- Knowledge Base List -->
                                    <div class="flex flex-col gap-2 flex-1 overflow-y-auto">
                                        @if(filteredKnowledgeBases.length > 0) {
                                        <div class="flex flex-col gap-2">
                                            @for(kb of filteredKnowledgeBases; track kb._id) {
                                            <div class="flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer"
                                                [class.bg-blue-50]="selectedKnowledgeBaseId === kb._id"
                                                [class.border-blue-300]="selectedKnowledgeBaseId === kb._id"
                                                [class.border-gray-200]="selectedKnowledgeBaseId !== kb._id"
                                                [class.hover:bg-gray-50]="selectedKnowledgeBaseId !== kb._id"
                                                (click)="onKnowledgeBaseClick(kb._id)">
                                                <i [innerHTML]="SVGS.KNOWLEDGE_BASE | sanitizeHtml" alt="" width="24"
                                                    height="24"></i>
                                                <div class="flex-1 min-w-0">
                                                    <span
                                                        class="text-sm font-medium text-gray-900 truncate block">{{kb.name}}</span>
                                                </div>
                                                @if(selectedKnowledgeBaseId === kb._id) {
                                                <i class="pi pi-chevron-right text-blue-600 text-sm"></i>
                                                }
                                            </div>
                                            }
                                        </div>
                                        } @else {
                                        <div class="flex items-center justify-center p-8">
                                            <p class="text-sm text-gray-500">No knowledge bases found</p>
                                        </div>
                                        }
                                    </div>
                                </div>

                                <!-- Right Panel: Files from Selected Knowledge Base -->
                                <div class="w-1/2 flex flex-col pl-4">
                                    @if(selectedKnowledgeBaseId) {
                                    @if(knowledgeBaseFilesLoading) {
                                    <div class="flex items-center justify-center p-8 flex-1">
                                        <p class="text-sm text-gray-500">Loading files...</p>
                                    </div>
                                    } @else {
                                    <!-- Search Bar for Files -->
                                    <div class="w-full mb-4">
                                        <app-search-bar placeholder="Search files"
                                            [(ngModel)]="knowledgeBaseFilesSearchValue"
                                            (searchChange)="onKnowledgeBaseFilesSearch()" width="100%">
                                        </app-search-bar>
                                    </div>

                                    <!-- Files List -->
                                    <div class="flex flex-col gap-2 flex-1 overflow-y-auto">
                                        @if(filteredKnowledgeBaseFiles.length > 0) {
                                        <div class="flex flex-col gap-2">
                                            @for(file of filteredKnowledgeBaseFiles; track file._id) {
                                            <div class="flex items-center gap-3 p-3 rounded-lg border transition-colors"
                                                [class.bg-orange-50]="isKnowledgeBaseFileSelected(file._id)"
                                                [class.border-orange-200]="isKnowledgeBaseFileSelected(file._id)"
                                                [class.border-gray-200]="!isKnowledgeBaseFileSelected(file._id)"
                                                [class.hover:bg-gray-50]="!isKnowledgeBaseFileSelected(file._id)">
                                                <p-checkbox [ngModel]="isKnowledgeBaseFileSelected(file._id)"
                                                    (ngModelChange)="setKnowledgeBaseFileSelectedState(file._id, $event)"
                                                    [binary]="true" [inputId]="'kb-file-' + file._id">
                                                </p-checkbox>
                                                <div class="flex-shrink-0">
                                                    @if(file.mimeType == "pdf"){
                                                    <i [innerHTML]="SVGS.PDF | sanitizeHtml" alt="" width="24"
                                                        height="24"></i>
                                                    }@else if (file.mimeType == "docx") {
                                                    <i [innerHTML]="SVGS.DOCX | sanitizeHtml" alt="" width="24"
                                                        height="24"></i>
                                                    }
                                                </div>
                                                <div class="min-w-0 flex-1">
                                                    <div class="text-sm font-medium text-gray-900 truncate"
                                                        pTooltip="{{ file.name }}" tooltipPosition="top">
                                                        {{ file.name }}
                                                    </div>
                                                    <div class="text-xs text-gray-500 truncate">
                                                        {{ file.size | fileSize }}
                                                    </div>
                                                </div>
                                            </div>
                                            }
                                        </div>
                                        } @else {
                                        <div class="flex items-center justify-center p-8">
                                            <p class="text-sm text-gray-500">No files found in this knowledge base</p>
                                        </div>
                                        }
                                    </div>
                                    }
                                    } @else {
                                    <div class="flex items-center justify-center p-8 flex-1">
                                        <p class="text-sm text-gray-500">Select a knowledge base to view files</p>
                                    </div>
                                    }
                                </div>
                            </div>
                            }
                        </p-tabpanel>
                    </p-tabpanels>
                </p-tabs>
            </div>
            }
            }
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end gap-2">
            @if(!showUploadSection && !showGenerateTemplateSection) {
            <app-button variant="filled" severity="primary"
                [label]="selectedCreationMethod === TEMPLATE_CREATION_METHOD.MANUAL_CREATE ? 'Create' : 'Continue'"
                (clicked)="onContinue()"
                [disabled]="!templateName.trim() || !templateId.trim() || uploadLoading || !selectedDocumentTypeId"
                [loading]="uploadLoading">
            </app-button>
            } @else if(showGenerateTemplateSection) {
            <div class="flex items-center gap-1 border border-gray-300 rounded-base p-1 px-2 cursor-pointer"
                (click)="onGenerateTemplate()"
                [class.opacity-50]="!generatePrompt || generatePrompt.trim().length === 0 || uploadLoading"
                [class.pointer-events-none]="!generatePrompt || generatePrompt.trim().length === 0 || uploadLoading">
                <img src="icons/template-library/ai_icon.svg" alt="Generate" class="w-5 h-5">
                <span class="text-md font-medium theme-text">Generate</span>
            </div>
            } @else if(showFilePreview) {
            <app-button variant="filled" severity="primary" label="Upload" [disabled]="!canUpload || uploadLoading"
                [loading]="uploadLoading" (clicked)="onUploadSelected()">
            </app-button>
            } @else if(showFileSelectionView) {
            <app-button variant="filled" severity="primary" label="Upload" [disabled]="!canUpload || uploadLoading"
                [loading]="uploadLoading" (clicked)="onUploadSelected()">
            </app-button>
            } @else {
            <app-button variant="filled" severity="primary" label="Upload"
                [disabled]="uploadedLocalFiles.length === 0 || uploadLoading" [loading]="uploadLoading"
                (clicked)="onUploadSelected()">
            </app-button>
            }
        </div>
    </ng-template>
</app-dialog>

<!-- Template Generation Status Dialog -->
<app-dialog [(visible)]="showTemplateGenerationDialog" title="Generate template" [closable]="false"
    [showCloseButton]="false" (closed)="onCloseTemplateGenerationDialog()" class="max-w-[32rem]"
    [fullscreenOnMobile]="true">
    <ng-template #bodyTemplate>
        @if (isTemplateCreditsExhausted(generatingTemplateId)) {
        <div class="flex flex-col min-h-[22rem]">
            <app-credits-overview variant="exhaustionlayout" feature="GENERATE_WITH_AI" mode="agent"
                [featureId]="generatingTemplateId" [externalResume]="true" label="Restart"
                (resume)="onTemplateGenerationRetry()" />
        </div>
        } @else {
        <div class="flex flex-col items-center gap-2 py-2 p-4">
            <div class="relative flex items-center justify-center w-full h-44">
                @if (templateGenerationAnimationData) {
                <div class="relative">
                    <app-lottie-animation [animationData]="templateGenerationAnimationData" [loop]="true"
                        [autoplay]="true" width="300px" height="300px" containerClass="template-generation-lottie">
                    </app-lottie-animation>
                </div>
                }
            </div>
            <div class="flex flex-col gap-2 text-center mt-2">
                <p class="text-sm text-gray-900 leading-relaxed">
                    We're generating your template and extracting structure. You'll be notified once it's ready.
                </p>
            </div>
        </div>
        }
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end">
            <app-button variant="outlined" severity="contrast" label="Hide"
                (clicked)="onCloseTemplateGenerationDialog()">
            </app-button>
        </div>
    </ng-template>
</app-dialog>

<!-- Edit Template Dialog -->
<app-dialog [(visible)]="showEditTemplateDialog" [closable]="false" [showCloseButton]="false"
    (closed)="onCloseEditTemplateDialog()" class="max-w-[95vw] h-[95vh]" [fullscreenOnMobile]="true">
    <ng-template #headerTemplate>
        <hlm-dialog-header>
            <div class="flex items-center justify-between w-full">
                <h2 class="text-lg font-semibold text-gray-900">{{selectedTemplate.name}}</h2>
                <div class="flex items-center gap-2">
                    <app-button variant="outlined" severity="contrast" label="Download"
                        (clicked)="onDownloadCurrentTemplate()" icon="pi pi-download" iconSize="default">
                    </app-button>
                    <app-button variant="outlined" (clicked)="onCloseEditTemplateDialog()">
                        <i class="pi pi-times text-lg"></i>
                    </app-button>
                </div>
            </div>
        </hlm-dialog-header>
    </ng-template>
    <ng-template #bodyTemplate>
        <app-view-template #viewTemplate [templateLibraryId]="selectedTemplateId" (close)="onCloseEditTemplateDialog()">
        </app-view-template>
    </ng-template>
</app-dialog>

<!-- Quick Edit Dialog (Metadata Only) -->
<app-dialog [(visible)]="showQuickEditDialog" title="Edit template" (closed)="onCloseQuickEditDialog()"
    class="max-w-[32rem]" [fullscreenOnMobile]="true">
    <ng-template #bodyTemplate>
        <div class="flex flex-col gap-4 p-4">
            <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-gray-600">
                    Template name<span class="text-red-500 ml-1">*</span>
                </label>
                <input pInputText class="w-full border rounded-base p-1 border-gray-300"
                    [(ngModel)]="quickEditTemplateName" name="quickEditTemplateName" placeholder="Type here" required />
            </div>
            <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-gray-700">
                    Template ID<span class="text-red-500 ml-1">*</span>
                </label>
                <input pInputText class="w-full border rounded-base p-1 border-gray-300"
                    [(ngModel)]="quickEditTemplateId" name="quickEditTemplateId" placeholder="Type here" required />
            </div>
            <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-gray-700">Description</label>
                <input pInputText class="w-full border rounded-base p-1 border-gray-300"
                    [(ngModel)]="quickEditDescription" name="quickEditDescription" placeholder="Type here" />
            </div>
            <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-gray-600">
                    Document type<span class="text-red-500 ml-1">*</span>
                </label>
                <app-select [(value)]="quickEditDocumentTypeId" [options]="documentTypeOptions"
                    [config]="documentTypeConfig" name="quickEditDocumentType">
                </app-select>
            </div>
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end gap-2">
            <app-button variant="filled" severity="primary" label="Save" (clicked)="onSaveQuickEdit()"
                [disabled]="!quickEditTemplateName || !quickEditTemplateId || !quickEditDocumentTypeId || quickEditLoading"
                [loading]="quickEditLoading">
            </app-button>
        </div>
    </ng-template>
</app-dialog>

<!-- Clone Template Confirmation Dialog -->
<app-dialog [(visible)]="showCloneConfirmDialog" [closable]="false" [showCloseButton]="false"
    (closed)="onCloseCloneConfirmDialog()" class="max-w-[30rem]" [fullscreenOnMobile]="true">
    <ng-template #headerTemplate>
        <hlm-dialog-header>
            <div class="flex items-start gap-3">
                <i class="pi pi-exclamation-triangle text-3xl text-orange-500 mt-2"></i>
                <div class="flex flex-col gap-1">
                    <h2 class="text-base font-semibold text-gray-900">Are you sure you want to clone this template?
                    </h2>
                    <p class="text-sm text-gray-500">A duplicate copy will be created that you can edit independently.
                    </p>
                </div>
            </div>
        </hlm-dialog-header>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end gap-2">
            <app-button variant="outlined" severity="contrast" label="Cancel" (clicked)="onCloseCloneConfirmDialog()">
            </app-button>
            <app-button variant="filled" severity="primary" label="Yes, Continue" (clicked)="onConfirmClone()"
                [disabled]="cloneLoading" [loading]="cloneLoading">
            </app-button>
        </div>
    </ng-template>
</app-dialog>

<!-- Delete Template Confirmation Dialog -->
<app-dialog [(visible)]="showDeleteConfirmDialog" [closable]="false" [showCloseButton]="false"
    (closed)="onCloseDeleteConfirmDialog()" class="max-w-[28rem]" [fullscreenOnMobile]="true">
    <ng-template #headerTemplate>
        <hlm-dialog-header>
            <div class="flex items-start gap-3">
                <i class="pi pi-exclamation-triangle text-3xl text-orange-500 mt-2"></i>
                <div class="flex flex-col gap-1">
                    <h2 class="text-base font-semibold text-gray-900">Are you sure you want to delete this template?
                    </h2>
                    <p class="text-sm text-gray-500">This action can't be undone and it won't affect any active
                        documents using it.</p>
                </div>
            </div>
        </hlm-dialog-header>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end gap-2">
            <app-button variant="outlined" severity="contrast" label="Cancel" (clicked)="onCloseDeleteConfirmDialog()">
            </app-button>
            <app-button variant="filled" severity="danger" label="Yes, delete" (clicked)="onConfirmDelete()"
                [disabled]="deleteLoading" [loading]="deleteLoading">
            </app-button>
        </div>
    </ng-template>
</app-dialog>

<!-- Bulk Download Confirmation Dialog -->
<app-dialog [(visible)]="showBulkDownloadConfirmDialog" [closable]="false" [showCloseButton]="false"
    (closed)="onCloseBulkDownloadConfirmDialog()" class="max-w-[28rem]" [fullscreenOnMobile]="true">
    <ng-template #headerTemplate>
        <hlm-dialog-header>
            <div class="flex items-start gap-3">
                <i class="pi pi-exclamation-triangle text-3xl text-orange-500 mt-2"></i>
                <div class="flex flex-col gap-1">
                    <h2 class="text-base font-semibold text-gray-900">Are you sure you want to download all these
                        templates?</h2>
                </div>
            </div>
        </hlm-dialog-header>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end gap-2">
            <app-button variant="outlined" severity="contrast" label="Cancel"
                (clicked)="onCloseBulkDownloadConfirmDialog()">
            </app-button>
            <app-button variant="filled" severity="primary" label="Download" (clicked)="onConfirmBulkDownload()"
                [disabled]="bulkDownloadLoading" [loading]="bulkDownloadLoading">
            </app-button>
        </div>
    </ng-template>
</app-dialog>

<!-- Bulk Delete Confirmation Dialog -->
<app-delete-confirmation [(visible)]="showBulkDeleteConfirmDialog"
    headerText="Are you sure you want to delete these templates?"
    bodyText="This action can't be undone and it won't affect any active documents using them."
    confirmButtonText="Yes, delete" [loading]="bulkDeleteLoading" (onConfirm)="onConfirmBulkDelete()"
    (onCancel)="onCloseBulkDeleteConfirmDialog()">
</app-delete-confirmation>

<!-- Workspace Mismatch Dialog -->
<app-dialog [(visible)]="workspaceMismatchDialog" title="You're in the Wrong Workspace" [closable]="false"
    [showCloseButton]="false" class="max-w-[30rem]" [fullscreenOnMobile]="true">
    <ng-template #bodyTemplate>
        <div class="p-4">
            <p class="text-sm text-gray-700">
                This template is available in a different workspace. Please switch to the appropriate workspace to
                access it.
            </p>
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end gap-2">
            <app-button label="Switch Workspace" (clicked)="switchToTemplateWorkspace()" severity="primary"
                variant="filled" icon="pi pi-sign-out" />
        </div>
    </ng-template>
</app-dialog>

<!-- File Selection Dialog -->
@if(openFileSelectionDialog) {
<app-file-selection [(visible)]="openFileSelectionDialog" [config]="fileSelectionConfig" [fetch]="fileSelectionFetch"
    [dropdownOptions]="fileSelectionDropdownOptions" (onSubmit)="onFileSelectionSubmit($event)"
    (onCancel)="onFileSelectionCancel()">
</app-file-selection>
}